import { DEFAULT_ANIMATION_SPEED } from '@src/config/imageSettings';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  type ArborElementAnimationKeyframe,
  parseElementAnimationKeyframes,
} from '@src/utility/elementAnimationUtils';
import lodash from 'lodash';
import React, { Fragment } from 'react';
import styled from 'styled-components';

import SliderSelect from '../common/PlainSlider';
import PropertyDropdown from '../common/PropertyDropdown';
import { PropertyCaption } from './PropertyCaption';

import type { ElementState } from '../../renderer/ElementState';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

interface ElementAnimationSettingsProps {
  activeElement: ElementState;
  editorStore: EditorStoreLike;
}

function hasAnimationsField(x: unknown): x is { animations?: unknown } {
  return !!x && typeof x === 'object' && 'animations' in x;
}

export const ElementAnimationSettings: React.FC<
  ElementAnimationSettingsProps
> = props => {
  const videoCreator = useVideoCreatorStore();
  const editorStore = props.editorStore;
  const { source } = props.activeElement;

  let animations: ArborElementAnimationKeyframe[] = [];
  if (hasAnimationsField(source)) {
    animations = parseElementAnimationKeyframes(source.animations);
  }

  // const [prevDuration, setPrevDuration] = useState(duration);

  const elementAnimation =
    animations?.find(kf => kf && kf.arbor_subType)?.arbor_subType ?? 'none';

  // // if duration changes we need to update the animation speed
  // useEffect(() => {
  //   if (animations) {
  //     const speed = speedLookup(
  //       animations?.find((keyframe: any) => !!keyframe.arbor_subType),
  //     );

  //     if (speed) {
  //       let newSpeed = speed;
  //       if (prevDuration !== 0 && duration !== 0) {
  //         newSpeed = `${(parseFloat(speed) / prevDuration) * duration}%`;
  //       }
  //       console.log('newSpeed', newSpeed);
  //       setAnimationDebounce(elementAnimation, newSpeed);
  //       setPrevDuration(duration);
  //     }
  //   }
  // }, [duration]);

  const speedLookup = (keyframe: ArborElementAnimationKeyframe | undefined) => {
    if (!keyframe?.arbor_subType) return null;
    switch (keyframe.arbor_subType) {
      case 'zoomOut':
        return keyframe.start_scale || DEFAULT_ANIMATION_SPEED;
      case 'zoomIn':
        return keyframe.end_scale || DEFAULT_ANIMATION_SPEED;
      case 'panLeft':
      case 'panRight':
      case 'panUp':
      case 'panDown':
        return keyframe.end_scale || DEFAULT_ANIMATION_SPEED;
      case 'panLeftWithZoom':
      case 'panRightWithZoom':
      case 'panUpWithZoom':
      case 'panDownWithZoom':
        return keyframe.end_scale || DEFAULT_ANIMATION_SPEED;
      default:
        return null;
    }
  };

  const elementAnimationSpeed = speedLookup(
    animations?.find(k => k && k.arbor_subType),
  );

  // Helper function to modify animations using headless editor or fallback to legacy
  const modifyAnimations = (newAnimations: ArborElementAnimationKeyframe[]) => {
    if (!props.activeElement?.source?.id) return;
    editorStore.editor.commandUpdateElement(props.activeElement.source.id, {
      animations: newAnimations,
    });
  };

  const animationTypeLookup = (subType: string) => {
    switch (subType) {
      case 'zoomIn':
      case 'zoomOut':
        return 'scale';
      case 'none':
        return 'none';
      default:
        return 'pan';
    }
  };

  const setAnimation = (
    subType: string | null = null,
    speed: string | null = null,
  ) => {
    const newAnimations: ArborElementAnimationKeyframe[] = animations
      ? animations.filter(kf => kf && kf.time)
      : [];

    const effectiveSubType = subType ?? 'none';
    const type = animationTypeLookup(effectiveSubType);

    if (type !== 'none') {
      const animation: ArborElementAnimationKeyframe = { type };

      if (effectiveSubType !== 'none') {
        animation.arbor_subType = effectiveSubType;
        animation.scope = 'element';
        animation.easing = 'linear';
        switch (effectiveSubType) {
          case 'zoomIn':
            animation.start_scale = '100%';
            animation.end_scale = speed || DEFAULT_ANIMATION_SPEED;
            animation.fade = false;
            break;
          case 'zoomOut':
            animation.start_scale = speed || DEFAULT_ANIMATION_SPEED;
            animation.end_scale = '100%';
            animation.fade = false;
            break;
          case 'panLeft':
            animation.start_x = '100%';
            animation.start_y = '50%';
            animation.end_x = '0%';
            animation.end_y = '50%';
            animation.fade = false;
            animation.start_scale = speed || DEFAULT_ANIMATION_SPEED;
            animation.end_scale = speed || DEFAULT_ANIMATION_SPEED;
            break;
          case 'panRight':
            animation.start_x = '0%';
            animation.start_y = '50%';
            animation.end_x = '100%';
            animation.end_y = '50%';
            animation.fade = false;
            animation.start_scale = speed || DEFAULT_ANIMATION_SPEED;
            animation.end_scale = speed || DEFAULT_ANIMATION_SPEED;
            break;
          case 'panUp':
            animation.start_x = '50%';
            animation.start_y = '100%';
            animation.end_x = '50%';
            animation.end_y = '0%';
            animation.fade = false;
            animation.start_scale = speed || DEFAULT_ANIMATION_SPEED;
            animation.end_scale = speed || DEFAULT_ANIMATION_SPEED;
            break;
          case 'panDown':
            animation.start_x = '50%';
            animation.start_y = '0%';
            animation.end_x = '50%';
            animation.end_y = '100%';
            animation.fade = false;
            animation.start_scale = speed || DEFAULT_ANIMATION_SPEED;
            animation.end_scale = speed || DEFAULT_ANIMATION_SPEED;
            break;
          case 'panLeftWithZoom':
            animation.start_x = '100%';
            animation.start_y = '50%';
            animation.end_x = '0%';
            animation.end_y = '50%';
            animation.start_scale = '100%';
            animation.end_scale = speed || DEFAULT_ANIMATION_SPEED;
            animation.fade = false;
            break;
          case 'panRightWithZoom':
            animation.start_x = '0%';
            animation.start_y = '50%';
            animation.end_x = '100%';
            animation.end_y = '50%';
            animation.start_scale = '100%';
            animation.end_scale = speed || DEFAULT_ANIMATION_SPEED;
            animation.fade = false;
            break;
          case 'panUpWithZoom':
            animation.start_x = '50%';
            animation.start_y = '100%';
            animation.end_x = '50%';
            animation.end_y = '0%';
            animation.start_scale = '100%';
            animation.end_scale = speed || DEFAULT_ANIMATION_SPEED;
            animation.fade = false;
            break;
          case 'panDownWithZoom':
            animation.start_x = '50%';
            animation.start_y = '0%';
            animation.end_x = '50%';
            animation.end_y = '100%';
            animation.start_scale = '100%';
            animation.end_scale = speed || DEFAULT_ANIMATION_SPEED;
            animation.fade = false;
            break;
          default:
            break;
        }
      }

      newAnimations.push(animation);
    }

    modifyAnimations(newAnimations);
  };

  const setAnimationDebounce = lodash.debounce(setAnimation, 250);

  const animationEntries = videoCreator.isImageElementComposition(
    props.activeElement,
  )
    ? BlackFrameSettings
    : ElementAnimationTypes;

  // If element source missing minimal shape/image fields, bail out quietly
  if (!props.activeElement?.source?.id) {
    return null;
  }

  return (
    <Fragment>
      <PropertyCaption>Element Animation</PropertyCaption>
      <PropertyDropdown
        value={elementAnimation}
        onChange={value => {
          setAnimation(value, elementAnimationSpeed);
        }}
        defaultValue={elementAnimation}
        values={[
          { caption: 'None', value: 'none' },
          ...Object.entries(animationEntries).map(([type, caption]) => ({
            caption,
            value: type,
          })),
        ]}
      />

      {elementAnimation === 'none' ? null : (
        <>
          <PropertyCaption>Element Animation Speed</PropertyCaption>
          <SliderWrapper>
            <SliderSelect
              getValue={newValue =>
                setAnimationDebounce(elementAnimation, newValue + '%')
              }
              defaultValue={elementAnimationSpeed}
              currValue={
                elementAnimationSpeed
                  ? parseFloat(elementAnimationSpeed)
                  : undefined
              }
              min={101}
              max={145}
              showInput={false}
              ticks={[
                { value: 108, label: 'Very Slow' },
                { value: 118, label: 'Slow' },
                { value: 130, label: 'Normal' },
                { value: 140, label: 'Fast' },
              ]}
            />
          </SliderWrapper>
        </>
      )}
    </Fragment>
  );
};

const ElementAnimationTypes = {
  zoomIn: 'Zoom In',
  zoomOut: 'Zoom Out',
  panLeft: 'Pan Left',
  panRight: 'Pan Right',
  panUp: 'Pan Up',
  panDown: 'Pan Down',
  panLeftWithZoom: 'Pan Left With Zoom',
  panRightWithZoom: 'Pan Right With Zoom',
  panUpWithZoom: 'Pan Up With Zoom',
  panDownWithZoom: 'Pan Down With Zoom',
};

const BlackFrameSettings = {
  zoomIn: 'Zoom In',
  zoomOut: 'Zoom Out',
  panUp: 'Pan Up',
  panDown: 'Pan Down',
  panUpWithZoom: 'Pan Up With Zoom',
  panDownWithZoom: 'Pan Down With Zoom',
};

// (Removed unused AnimationSpeedProps interface)

const SliderWrapper = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 12px;
`;
